<template>
    <input
        :id="id"
        ref="input"
        class="PhoneNumberInput"
        type="text"
        @input="onInput"
        @change="onChange"
        @countrychange="onChange"
    >
</template>

<script lang="ts">
    import intlTelInput, { Plugin as IntlTelInputPlugin } from 'intl-tel-input';
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

    import 'intl-tel-input/build/js/utils';
    import PhoneNumberValue from '@/fields/interfaces/PhoneNumber';

    @Component({
        emits: [ 'update:modelValue' ],
        // PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): `emitChangeEvent` constructs a
        // typed `PhoneNumberValue` payload from intl-tel-input's own state, not the
        // raw native input event -- it's not a simple passthrough. Vue 3 merges
        // listeners into `$attrs`, so without `inheritAttrs: false` a caller's
        // `@input` would now ALSO auto-fall-through onto this root `<input>` as a
        // second, independent native listener, receiving the raw native Event
        // instead of the constructed value and double-firing the handler.
        inheritAttrs: false,
        // PRODUCT-26110 Task 12 (COMPONENT_V_MODEL): migrated to the Vue 3 v-model
        // contract. Consumed via v-model by fields/PhoneNumber.vue, and manually
        // (:model-value/@update:model-value) by forms/PhoneDelegationInput.vue.
    })
    export default class PhoneNumberInput extends Vue {
        @Prop({ type: String, default: undefined })
        private id!: string;

        @Prop({ default: '' })
        private modelValue!: string | PhoneNumberValue;

        private intlTelInput: IntlTelInputPlugin | null = null;
        private internalChangeInProgress: boolean = false;

        private get telInput(): Element | null {
            return this.intlTelInput ? (this.intlTelInput as any).telInput : null;
        }

        private get input() {
            return this.$refs.input as HTMLInputElement;
        }

        private mounted() {
            this.intlTelInput = intlTelInput(this.input);
            this.setInternalValue(this.modelValue);
        }

        private beforeUnmount() {
            if (this.intlTelInput && this.telInput) {
                // intlTelInput has problems with detaching from DOM on Vue component destroy,
                // So this small hack will isolate and remove the input safely.
                const div = document.createElement('div');
                div.appendChild(this.telInput);
                document.body.appendChild(div);
                this.intlTelInput.destroy();

                if (this.input && this.input.parentNode) {
                    this.input.parentNode.removeChild(this.input);
                }
            }
        }

        private setInternalValue(rawValue: string | PhoneNumberValue) {
            if (!this.intlTelInput) {
                throw new Error('IntlTelInput is not initialized.');
            }

            this.internalChangeInProgress = true;

            const value = typeof rawValue === 'string' ? { number: rawValue, country: null } : rawValue;

            if (value) {
                if (value.country) {
                    this.intlTelInput.setCountry(value.country);
                }

                if (value.number && value.number !== this.input.value) {
                    this.intlTelInput.setNumber(value.number);
                }
            } else if (!value) {
                this.intlTelInput.setNumber('');
            }

            this.internalChangeInProgress = false;
        }

        @Watch('modelValue')
        private onExternalChange(value: string | PhoneNumberValue) {
            if (!this.intlTelInput) {
                throw new Error('IntlTelInput is not initialized.');
            }

            this.setInternalValue(value);
        }

        private onInput() {
            if (this.internalChangeInProgress) {
                return;
            }
            this.emitChangeEvent();
        }

        private onChange() {
            if (!this.intlTelInput) {
                throw new Error('IntlTelInput is not initialized.');
            }
            this.intlTelInput.setNumber(this.input.value);
            this.emitChangeEvent();
        }

        private emitChangeEvent() {
            if (!this.intlTelInput) {
                throw new Error('IntlTelInput is not initialized.');
            }

            if (this.internalChangeInProgress) {
                return;
            }

            const phone = this.input.value.replace(/\D/g, '');
            const valid = Boolean(this.intlTelInput.isValidNumber());
            const countryData = this.intlTelInput.getSelectedCountryData();

            this.$emit('update:modelValue', {
                number: phone,
                dialCode: countryData.dialCode,
                country: countryData.iso2,
                qualifiedNumber: valid ? `+${countryData.dialCode || 1} ${phone}` : null,
                valid,
            });
        }
    }
</script>
